// ============================================================
// TERROR TCG — RESPONSE BRIDGE
// ============================================================
// The nervous system of the RESPONSE WINDOW (Rulez v2 amendment,
// July 2026): "No declared action resolves while your adversary
// still holds an answer to it."
//
// WHY THIS FILE EXISTS
// The NPC engine (NPC.js) is driven from App.js — a separate React
// tree from Shatterfield. They share only Firebase. This module is
// the one thing BOTH import, so neither ever imports the other:
// zero circular-import risk, and the future PvP session can slot a
// Firebase-backed responder in here with ZERO changes to NPC.js.
//
// FLOW
//   1. Shatterfield (NPC matches only) registers a responder on
//      mount and unregisters on unmount.
//   2. NPC.js calls requestWindow(action) BEFORE resolving any
//      response-worthy action:
//        { kind: "LYT_ATTACK",      targetIdx, lyt }
//        { kind: "ANIMA_ATTACK",    lyt }
//        { kind: "LYT_ATTACK_TOME", lyt }
//        { kind: "ACTIVATION",      cardId }
//   3. The responder (Shatterfield) decides whether a window even
//      MATERIALIZES — only when the defender holds a legal,
//      affordable reaction RIGHT NOW. This is the 4xS anti-prompt-
//      fest law: option C on paper (every action passes through a
//      window), zero dead prompts in practice.
//   4. Resolution payload:
//        { responded: false }                          — no window,
//          passed, snoozed, or timed out. Action resolves clean.
//        { responded: true, counter: null }            — a generic
//          interrupt fired (defensive instant, buff, flip). The
//          action STILL RESOLVES, but against the player's FRESH
//          state — the caller re-reads before resolving.
//        { responded: true, counter: "nihil"|"omnia",
//          late, counterName }                        — a true
//          counter fired vs an ACTIVATION. Nihil = effect voided;
//          Omnia = the activated card is ruined. Fare stays paid
//          (canon: activation fare is paid once).
//          COUNTER HIERARCHY (caP'n ruling, July 2026): late:true
//          means a L1/L2 counter answered a L3+ activation — it
//          cannot pre-empt. The effect resolves FIRST, then the
//          activated card must leave the field. L1 stops L2 fine;
//          only Level 3 or greater cards get the shield.
//
// SAFETY
//   • No responder registered (PvP match, menus, tutorial,
//     unmount) → resolves immediately. Zero pause, zero cost.
//   • A hard failsafe timer guarantees the NPC turn can NEVER
//     soft-lock on a vanished window (screen exit, crash, unmount
//     mid-window). The window UI's own 5s countdown resolves first
//     in all healthy flows — the failsafe is the seatbelt.
// ============================================================

const NO_RESPONSE = Object.freeze({ responded: false });

// Hard ceiling — generous past the UI's 5s so slow devices never
// get their window yanked, tight enough that a dead window can't
// hold the NPC hostage.
const FAILSAFE_MS = 15000;

let responder = null;

/**
 * Shatterfield registers its window-opener here (NPC matches only).
 * The registered fn receives the action and returns a Promise of a
 * resolution payload (see FLOW above).
 */
export const registerResponder = (fn) => {
  responder = typeof fn === "function" ? fn : null;
};

/** Shatterfield unregisters on unmount — requests flow free again. */
export const unregisterResponder = () => {
  responder = null;
};

/** True while a responder is live (debug/inspection convenience). */
export const hasResponder = () => !!responder;

/**
 * The NPC engine's single call. Declares an action and awaits the
 * adversary's answer (or the all-clear). NEVER throws, NEVER hangs.
 */
export const requestWindow = async (action) => {
  if (!responder) return NO_RESPONSE;
  try {
    const result = await Promise.race([
      Promise.resolve(responder(action)),
      new Promise((res) => setTimeout(() => res(NO_RESPONSE), FAILSAFE_MS)),
    ]);
    return result && typeof result === "object" ? result : NO_RESPONSE;
  } catch (e) {
    console.warn("[ResponseBridge] responder threw — resolving clean:", e?.message);
    return NO_RESPONSE;
  }
};

export default { registerResponder, unregisterResponder, hasResponder, requestWindow };
